// src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Helper function to centralize successful auth tracking data
   */
  const handleAuthSuccess = (response) => {
    if (response.token) {
      localStorage.setItem('token', response.token);
      localStorage.setItem('role', response.role); 
      localStorage.setItem('user', JSON.stringify({
        id: response.id,
        email: response.email,
        fullName: response.fullName,
        role: response.role
      }));
    }

    // 1. Update internal context synchronization states
    setUser({
      id: response.id,
      email: response.email,
      fullName: response.fullName,
      role: response.role,
    });
    setIsAuthenticated(true);
    
    // Ensure loading is set to false explicitly upon successful login
    setLoading(false); 
    
    return response;
  };

  // Re-hydrate session state from localStorage on application bootstrap mount
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      const currentUser = authService.getCurrentUser();
      
      if (token && currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Failed to rehydrate auth session:", error);
      // Clear poisoned storage configurations if reading fails
      localStorage.clear(); 
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    // Force loading state on submission execution window
    setLoading(true); 
    try {
      const response = await authService.login(credentials);
      return handleAuthSuccess(response); 
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const loginWithGoogleData = async (backendResponse) => {
    setLoading(true);
    try {
      const normalizedResponse = {
        token: backendResponse.token,
        ...(backendResponse.user ? backendResponse.user : backendResponse)
      };
      return handleAuthSuccess(normalizedResponse); 
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signup = async (userData) => {
    try {
      return await authService.signup(userData); 
    } catch (error) {
      throw error;
    }
  };

  const verifyOtp = async (otpData) => {
    setLoading(true);
    try {
      const response = await authService.verifyOtp(otpData);
      return handleAuthSuccess(response); 
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginWithGoogleData,
    signup,
    verifyOtp,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider context wrapper hierarchy');
  }
  return context;
};

export default AuthContext;